import { ContentBlock, ContentBlockType } from '../types/executiveChat';

/**
 * Parse Executive Chat AI response into structured content blocks
 * ONLY FOR EXECUTIVE CHAT - Does not affect other components
 * 
 * Supports:
 * - Regular markdown text
 * - ```json:type blocks for charts/cards
 * - :::highlight blocks
 */
export function parseExecutiveChatResponse(response: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  let currentText = '';
  let blockId = 0;

  // Split by code blocks and highlight blocks
  const lines = response.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Check for JSON code blocks: ```json:type
    if (line.startsWith('```json:')) {
      // Extract type
      const typeMatch = line.match(/```json:(\S+)/);
      const rawType = typeMatch ? typeMatch[1] : 'text';
      
      // Skip recommended-questions blocks entirely - they're handled separately
      if (rawType === 'recommended-questions') {
        // Find the closing backticks and skip the entire block
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          i++;
        }
        if (i < lines.length && lines[i].startsWith('```')) {
          i++; // Skip closing ```
        }
        continue;
      }
      
      // Cast to ContentBlockType for known types
      const blockType = rawType as ContentBlockType;
      
      // Save any accumulated text
      if (currentText.trim()) {
        blocks.push({
          type: 'text',
          content: currentText.trim(),
          id: `block-${blockId++}`
        });
        currentText = '';
      }

      // Collect JSON content until closing ```
      i++;
      let jsonContent = '';
      let foundClosing = false;
      while (i < lines.length && !lines[i].startsWith('```')) {
        jsonContent += lines[i] + '\n';
        i++;
      }

      // Check if we found the closing backticks
      if (i < lines.length && lines[i].startsWith('```')) {
        foundClosing = true;
      }

      // Only try to parse if we have a complete block
      if (foundClosing) {
        try {
          const parsedData = JSON.parse(jsonContent.trim());
          blocks.push({
            type: blockType,
            content: parsedData,
            id: `block-${blockId++}`
          });
          i++; // Skip closing ```
          continue;
        } catch (error) {
          console.error(`Failed to parse ${blockType} block:`, error);
          // Fall back to treating as text
          currentText += line + '\n' + jsonContent;
        }
      } else {
        // Incomplete block (still streaming) - treat as text for now
        currentText += line + '\n' + jsonContent;
      }

      continue;
    }

    // Check for highlight blocks: :::highlight type="success"
    if (line.startsWith(':::highlight')) {
      // Save any accumulated text
      if (currentText.trim()) {
        blocks.push({
          type: 'text',
          content: currentText.trim(),
          id: `block-${blockId++}`
        });
        currentText = '';
      }

      // Extract type
      const typeMatch = line.match(/type="(\w+)"/);
      const highlightType = typeMatch ? typeMatch[1] : 'info';

      // Collect content until closing :::
      i++;
      let highlightContent = '';
      while (i < lines.length && !lines[i].startsWith(':::')) {
        highlightContent += lines[i] + '\n';
        i++;
      }

      blocks.push({
        type: 'highlight',
        content: {
          type: highlightType,
          content: highlightContent.trim()
        },
        id: `block-${blockId++}`
      });

      i++; // Skip closing :::
      continue;
    }

    // Check for map blocks: :::map
    if (line.startsWith(':::map')) {
      // Save any accumulated text
      if (currentText.trim()) {
        blocks.push({
          type: 'text',
          content: currentText.trim(),
          id: `block-${blockId++}`
        });
        currentText = '';
      }

      // Collect JSON content until closing :::
      i++;
      let mapContent = '';
      let foundClosing = false;
      while (i < lines.length && !lines[i].startsWith(':::')) {
        mapContent += lines[i] + '\n';
        i++;
      }

      // Check if we found the closing
      if (i < lines.length && lines[i].startsWith(':::')) {
        foundClosing = true;
      }

      if (foundClosing && mapContent.trim()) {
        try {
          const parsedMap = JSON.parse(mapContent.trim());
          blocks.push({
            type: 'map',
            content: parsedMap,
            id: `block-${blockId++}`
          });
          i++; // Skip closing :::
          continue;
        } catch (error) {
          console.error('Failed to parse map block:', error);
          // Fall back to treating as text
          currentText += line + '\n' + mapContent;
        }
      } else {
        // Incomplete block (still streaming) - treat as text
        currentText += line + '\n' + mapContent;
      }

      i++; // Skip closing :::
      continue;
    }

    // Regular content - accumulate
    currentText += line + '\n';
    i++;
  }

  // Add any remaining text
  if (currentText.trim()) {
    blocks.push({
      type: 'text',
      content: currentText.trim(),
      id: `block-${blockId++}`
    });
  }

  return blocks;
}

/**
 * Extract recommended next questions from AI response
 * Returns an array of question strings
 */
export function parseRecommendedQuestions(content: string): string[] {
  const regex = /```json:recommended-questions\s*\n([\s\S]*?)```/g;
  const match = regex.exec(content);
  
  if (match && match[1]) {
    try {
      const questions = JSON.parse(match[1].trim());
      if (Array.isArray(questions)) {
        return questions.filter(q => typeof q === 'string' && q.trim().length > 0);
      }
    } catch (error) {
      console.error('Failed to parse recommended questions:', error);
      return [];
    }
  }
  
  return [];
}

/**
 * Remove recommended questions block from content for display
 * This prevents the JSON block from being rendered as markdown
 */
export function stripRecommendedQuestions(content: string): string {
  // Remove the entire recommended-questions JSON block
  const regex = /```json:recommended-questions\s*\n[\s\S]*?```/g;
  return content.replace(regex, '').trim();
}

/**
 * Extract the final report from an assistant message, dropping the model's intermediate
 * "thinking" narration.
 *
 * `message.content` concatenates every streamed chunk, so it includes the reasoning between tool
 * calls. Splits on the last `tool_result`, matching how the UI renders the message. Falls back to
 * the full content when there are no events or no tool calls.
 */
export function extractFinalReportContent(message: {
  content: string;
  events?: Array<{ type: 'text' | 'tool_use' | 'tool_result'; content?: string }>;
}): string {
  const events = message.events;
  if (!events || events.length === 0) return message.content;

  let lastToolResultIndex = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].type === 'tool_result') {
      lastToolResultIndex = i;
      break;
    }
  }

  if (lastToolResultIndex === -1) return message.content;

  const finalText = events
    .slice(lastToolResultIndex + 1)
    .filter(e => e.type === 'text')
    .map(e => e.content || '')
    .join('')
    .trim();

  return finalText.length > 0 ? finalText : message.content;
}

/**
 * Whether a message contains anything the Excel export can turn into a sheet.
 *
 * Mirrors the server's rule (executiveChatExportController.exportToExcel): at least one markdown
 * table, or a non-empty `json:kpi-cards` block. Used to disable the Excel button up front instead
 * of letting the request fail with a 400. Keep in step with the server if that rule changes.
 */
export function hasExportableTabularData(content: string): boolean {
  if (!content) return false;

  const lines = content.split('\n');

  // A markdown table needs a pipe row plus a separator row (|---|---|).
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.startsWith('|') || !line.endsWith('|')) continue;

    for (let j = i; j < lines.length; j++) {
      const row = lines[j].trim();
      if (!row.startsWith('|') || !row.endsWith('|')) break;
      if (/^\|[\s\-:|]+\|$/.test(row)) return true;
    }
  }

  // KPI cards are exported as a sheet when no tables are present.
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].trim().startsWith('```json:kpi-cards')) continue;

    let body = '';
    for (let j = i + 1; j < lines.length && !lines[j].startsWith('```'); j++) {
      body += lines[j] + '\n';
    }

    try {
      const parsed = JSON.parse(body.trim());
      if (Array.isArray(parsed) && parsed.length > 0) return true;
    } catch {
      // Malformed block — the server would also fail to parse it, so it does not count.
    }
  }

  return false;
}

